import React, { useState } from "react";

import {
    Typography,
    Box,
    Autocomplete,
    TextField,
    Divider,
    Button,
    Popover,
    Modal,
    Card,
    CardContent,
    Alert,
} from "@mui/material";

import { useMutation, useQueryClient } from "react-query";
import { deleteTechnician, updateTechnician } from "../query-functions";

const modalStyle = {
    margin: "auto",
    mt: {
        md: 6,
        lg: 8,
        xl: 10,
    },

    bgcolor: "background.paper",

    width: {
        xs: "100%",
        sm: "80%",
        md: "60%",
        lg: "50%",
        xl: "40%",
    },

    minHeight: "200px",
};

const defaultUpdateValues = {
    name: "",
    userId: "",
};

const ManageTechniciansView = ({
    technicians,
    open,
    manageButtonAnchorEl,
    setManageButtonAnchorEl,
    clientID,
    emitSnack,
}) => {
    const queryClient = useQueryClient();

    const [selectedTechnician, setSelectedTechnician] = useState();
    const [updates, setUpdates] = useState(defaultUpdateValues);

    const { mutate: deleteTech, isLoading: deleteTechIsLoading } = useMutation(
        deleteTechnician,
        {
            onSuccess: () => {
                emitSnack({
                    type: "success",
                    message: "Technician Deleted",
                });

                queryClient.invalidateQueries("technicians");
                queryClient.invalidateQueries("users");
                queryClient.invalidateQueries("technicianCalendarEvents");

                handleClose();
            },
        }
    );

    const { mutate: updateTech, isLoading: updateTechIsLoading } = useMutation(
        updateTechnician,
        {
            onSuccess: () => {
                emitSnack({
                    type: "success",
                    message: "Technician Updated",
                });

                queryClient.invalidateQueries("technicians");
                queryClient.invalidateQueries("technicianCalendarEvents");

                handleClose();
            },
        }
    );

    const handleUpdate = () => {
        updateTech({
            clientID,
            techID: selectedTechnician.techID,
            techName: updates.name,
        });
    };

    const handleDelete = () => {
        deleteTech({
            techID: selectedTechnician.techID,
            clientID,
        });
    };

    const handleClose = () => {
        setSelectedTechnician(null);
        setUpdates(defaultUpdateValues);
        setManageButtonAnchorEl(null);
    };
    return (
        <Popover
            open={open}
            onClose={() => setManageButtonAnchorEl(null)}
            anchorEl={manageButtonAnchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: 300,
                    minWidth: 250,
                    p: 2,
                }}
            >
                <Typography variant="subtitle1" lineHeight={1}>
                    Select a Technician
                </Typography>

                <Autocomplete
                    options={technicians.map((tech) => tech.techName)}
                    sx={{ mt: 1.5 }}
                    size="small"
                    onChange={(event, newValue) => {
                        const technician = technicians.find(
                            (tech) => tech.techName === newValue
                        );
                        setSelectedTechnician(technician);
                        setUpdates({
                            name: technician.techName,
                            backgroundColor: technician.backgroundColor,
                            userId: technician.userId,
                        });
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Technicians" />
                    )}
                />

                {selectedTechnician && (
                    <Modal
                        open={Boolean(selectedTechnician)}
                        onClose={handleClose}
                    >
                        <Card sx={modalStyle}>
                            <CardContent>
                                <Typography variant="h6">
                                    Technician: "{selectedTechnician.techName}"
                                </Typography>

                                {selectedTechnician.userId ? (
                                    <Alert severity="info">
                                        This technician is linked to a users
                                        account. It cannot be edited
                                    </Alert>
                                ) : (
                                    <TextField
                                        label="Name"
                                        type="text"
                                        size="small"
                                        value={updates.name}
                                        onChange={(ev) => {
                                            setUpdates({
                                                ...updates,
                                                name: ev.target.value,
                                            });
                                        }}
                                        sx={{
                                            mt: 1.5,
                                            width: "100%",
                                        }}
                                    />
                                )}
                            </CardContent>

                            <Divider />
                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Button
                                    onClick={handleDelete}
                                    color="error"
                                    variant="outlined"
                                    disabled={deleteTechIsLoading}
                                >
                                    Delete Technician
                                </Button>

                                <Box>
                                    <Button
                                        onClick={handleClose}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mr: 1 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={handleUpdate}
                                        disabled={
                                            deleteTechIsLoading ||
                                            updateTechIsLoading ||
                                            updates.name ===
                                                selectedTechnician.techName
                                        }
                                    >
                                        Save Changes
                                    </Button>
                                </Box>
                            </Box>
                        </Card>
                    </Modal>
                )}
            </Box>
        </Popover>
    );
};

export default ManageTechniciansView;
